import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { VoiceToTextTextarea as Textarea } from "@/components/voice-field";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Plus, Send, Pause, Play, Trash2, Pencil, Users, Clock, Eye, Phone, AlertTriangle, CheckCircle, XCircle, Loader2, RotateCcw } from "lucide-react";
import { describeSendError } from "@/lib/send-guard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

type SmsCampaign = {
  id: string;
  name: string;
  audienceType: "lead" | "client" | "mixed";
  filtersJson: string | null;
  messageTemplate: string;
  status: "draft" | "scheduled" | "sending" | "paused" | "completed";
  sendMode: "now" | "scheduled";
  scheduledFor: string | null;
  createdByUserId: string;
  sentCount: number;
  failedCount: number;
  createdAt: string;
  updatedAt: string;
};

type SmsRecipientPreview = {
  entityType: string;
  entityId: string;
  phoneE164: string | null;
  displayName: string;
  regionId: string | null;
  clientType: string | null;
  isSuppressed: boolean;
  rawPhone: string | null;
};

type PreviewStats = {
  total: number;
  valid: number;
  suppressed: number;
  invalid: number;
};

type SmsSuppression = {
  id: string;
  phoneE164: string;
  reason: string;
  source: string;
  createdByUserId: string | null;
  createdAt: string;
};

type Region = {
  id: string;
  name: string;
  code?: string;
};

type ClientType = {
  id: string;
  name: string;
  isActive: boolean;
};

export function SmsCampaignTab() {
  const { toast } = useToast();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState(false);
  const [isSuppressionsDialogOpen, setIsSuppressionsDialogOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<SmsCampaign | null>(null);
  const [previewRecipients, setPreviewRecipients] = useState<SmsRecipientPreview[]>([]);
  const [previewStats, setPreviewStats] = useState<PreviewStats | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    audienceType: "mixed" as "lead" | "client" | "mixed",
    messageTemplate: "",
    sendMode: "now" as "now" | "scheduled",
    scheduledFor: "",
    filters: {
      regionId: "",
      clientType: "",
      leadStatus: "",
    },
  });

  const { data: configStatus } = useQuery<{ configured: boolean; provider: string }>({
    queryKey: ["/api/sms/config-status"],
  });

  const { data: campaigns = [], isLoading: campaignsLoading } = useQuery<SmsCampaign[]>({
    queryKey: ["/api/sms-campaigns"],
  });

  const { data: regions = [] } = useQuery<Region[]>({
    queryKey: ["/api/regions"],
  });

  const { data: clientTypes = [] } = useQuery<ClientType[]>({
    queryKey: ["/api/client-types"],
  });

  const { data: suppressions = [] } = useQuery<SmsSuppression[]>({
    queryKey: ["/api/sms-suppressions"],
  });

  const createCampaignMutation = useMutation({
    mutationFn: (data: any) => apiRequest("POST", "/api/sms-campaigns", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/sms-campaigns"] });
      setIsCreateDialogOpen(false);
      resetForm();
      toast({ title: "SMS campaign created" });
    },
    onError: () => toast({ title: "Failed to create campaign", variant: "destructive" }),
  });

  const updateCampaignMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => apiRequest("PUT", `/api/sms-campaigns/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/sms-campaigns"] });
      setIsEditDialogOpen(false);
      setSelectedCampaign(null);
      toast({ title: "Campaign updated" });
    },
    onError: () => toast({ title: "Failed to update campaign", variant: "destructive" }),
  });

  const deleteCampaignMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/sms-campaigns/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/sms-campaigns"] });
      toast({ title: "Campaign deleted" });
    },
    onError: () => toast({ title: "Failed to delete campaign", variant: "destructive" }),
  });

  const queueCampaignMutation = useMutation({
    mutationFn: (id: string) => apiRequest("POST", `/api/sms-campaigns/${id}/queue`),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/sms-campaigns"] });
      toast({ title: `Campaign queued: ${data.queued} recipients ready, ${data.skipped} skipped` });
    },
    onError: () => toast({ title: "Failed to queue campaign", variant: "destructive" }),
  });

  const sendCampaignMutation = useMutation({
    mutationFn: (id: string) => apiRequest("POST", `/api/sms-campaigns/${id}/send`),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/sms-campaigns"] });
      const msg = data.dryRun 
        ? `Dry run completed: ${data.sent} messages would be sent`
        : `Campaign sent: ${data.sent} delivered, ${data.failed} failed`;
      toast({ title: msg });
    },
    onError: () => toast({ title: "Failed to send campaign", variant: "destructive" }),
  });

  const pauseCampaignMutation = useMutation({
    mutationFn: (id: string) => apiRequest("POST", `/api/sms-campaigns/${id}/pause`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/sms-campaigns"] });
      toast({ title: "Campaign paused" });
    },
    onError: () => toast({ title: "Failed to pause campaign", variant: "destructive" }),
  });

  const resumeCampaignMutation = useMutation({
    mutationFn: (id: string) => apiRequest("POST", `/api/sms-campaigns/${id}/resume`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/sms-campaigns"] });
      toast({ title: "Campaign resumed" });
    },
    onError: () => toast({ title: "Failed to resume campaign", variant: "destructive" }),
  });

  // "Edit & retry failed" — the user fixes their template once and we
  // re-queue every recipient that the SMS scheduler marked failed (most
  // commonly because of a leaked merge variable like `{{nope}}`).
  const [retryDialogOpen, setRetryDialogOpen] = useState(false);
  const [retryCampaign, setRetryCampaign] = useState<SmsCampaign | null>(null);
  const [retryTemplate, setRetryTemplate] = useState("");
  const [retryGuardTokens, setRetryGuardTokens] = useState<string[] | null>(null);

  const openRetryDialog = (campaign: SmsCampaign) => {
    setRetryCampaign(campaign);
    setRetryTemplate(campaign.messageTemplate);
    setRetryGuardTokens(null);
    setRetryDialogOpen(true);
  };

  const retryFailedSmsMutation = useMutation({
    mutationFn: ({ id, messageTemplate }: { id: string; messageTemplate: string }) =>
      apiRequest("POST", `/api/sms-campaigns/${id}/retry-failed`, { messageTemplate }),
    onSuccess: async (response: any) => {
      const data = await response.json().catch(() => ({}));
      queryClient.invalidateQueries({ queryKey: ["/api/sms-campaigns"] });
      setRetryDialogOpen(false);
      setRetryCampaign(null);
      setRetryGuardTokens(null);
      toast({ title: "Retry queued", description: data?.message || "Failed recipients re-queued." });
    },
    onError: (error: any) => {
      const { details, title, description } = describeSendError(error, "Failed to retry SMS campaign.");
      setRetryGuardTokens(details.isGuardError && details.tokens?.length ? details.tokens : null);
      toast({ title, description, variant: "destructive" });
    },
  });

  const removeSuppression = useMutation({
    mutationFn: (phone: string) => apiRequest("DELETE", `/api/sms-suppressions/${encodeURIComponent(phone)}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/sms-suppressions"] });
      toast({ title: "Suppression removed" });
    },
    onError: () => toast({ title: "Failed to remove suppression", variant: "destructive" }),
  });

  const resetForm = () => {
    setFormData({
      name: "",
      audienceType: "mixed",
      messageTemplate: "",
      sendMode: "now",
      scheduledFor: "",
      filters: {
        regionId: "",
        clientType: "",
        leadStatus: "",
      },
    });
  };

  const openEditDialog = (campaign: SmsCampaign) => {
    setSelectedCampaign(campaign);
    const filters = campaign.filtersJson ? JSON.parse(campaign.filtersJson) : {};
    setFormData({
      name: campaign.name,
      audienceType: campaign.audienceType,
      messageTemplate: campaign.messageTemplate,
      sendMode: campaign.sendMode,
      scheduledFor: campaign.scheduledFor ? new Date(campaign.scheduledFor).toISOString().slice(0, 16) : "",
      filters: {
        regionId: filters.regionId || "",
        clientType: filters.clientType || "",
        leadStatus: filters.leadStatus || "",
      },
    });
    setIsEditDialogOpen(true);
  };

  const handlePreview = async (campaign: SmsCampaign) => {
    setSelectedCampaign(campaign);
    setIsPreviewLoading(true);
    setIsPreviewDialogOpen(true);
    
    try {
      const response = await apiRequest("POST", `/api/sms-campaigns/${campaign.id}/preview-recipients`) as {
        recipients: SmsRecipientPreview[];
        stats: PreviewStats;
      };
      setPreviewRecipients(response.recipients);
      setPreviewStats(response.stats);
    } catch {
      toast({ title: "Failed to load preview", variant: "destructive" });
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const handleCreate = () => {
    const data = {
      name: formData.name,
      audienceType: formData.audienceType,
      messageTemplate: formData.messageTemplate,
      sendMode: formData.sendMode,
      scheduledFor: formData.sendMode === "scheduled" ? formData.scheduledFor : null,
      filtersJson: formData.filters.regionId || formData.filters.clientType || formData.filters.leadStatus
        ? formData.filters
        : null,
    };
    createCampaignMutation.mutate(data);
  };

  const handleUpdate = () => {
    if (!selectedCampaign) return;
    const data = {
      name: formData.name,
      audienceType: formData.audienceType,
      messageTemplate: formData.messageTemplate,
      sendMode: formData.sendMode,
      scheduledFor: formData.sendMode === "scheduled" ? formData.scheduledFor : null,
      filtersJson: formData.filters.regionId || formData.filters.clientType || formData.filters.leadStatus
        ? formData.filters
        : null,
    };
    updateCampaignMutation.mutate({ id: selectedCampaign.id, data });
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      draft: "bg-muted text-muted-foreground",
      scheduled: "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300",
      sending: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300",
      paused: "bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300",
      completed: "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300",
    };
    return <Badge className={styles[status] || ""}>{status}</Badge>;
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleString();
  };

  const characterCount = formData.messageTemplate.length;
  const segmentCount = Math.ceil(characterCount / 160) || 1;

  return (
    <div className="space-y-6">
      {!configStatus?.configured && (
        <Card className="border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-yellow-700 dark:text-yellow-400">
              <AlertTriangle className="w-5 h-5" />
              SMS Provider Not Configured
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-yellow-600 dark:text-yellow-300">
              SMS campaigns will run in dry-run mode until an SMS provider is configured. 
              An administrator can configure SMS provider credentials in the Admin Panel under Communications settings.
            </p>
          </CardContent>
        </Card>
      )}

      <div className="flex items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold">SMS Campaigns</h3>
          <p className="text-sm text-muted-foreground">Send text message campaigns to leads and clients</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setIsSuppressionsDialogOpen(true)} data-testid="button-view-suppressions">
            <Phone className="w-4 h-4 mr-2" />
            Suppressed ({suppressions.length})
          </Button>
          <Button onClick={() => { resetForm(); setIsCreateDialogOpen(true); }} data-testid="button-new-sms-campaign">
            <Plus className="w-4 h-4 mr-2" />
            New Campaign
          </Button>
        </div>
      </div>

      {campaignsLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : campaigns.length === 0 ? (
        <Card className="p-12 text-center">
          <Phone className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
          <h3 className="text-lg font-semibold mb-2">No SMS Campaigns</h3>
          <p className="text-muted-foreground mb-4">Create your first SMS campaign to start messaging contacts.</p>
          <Button onClick={() => { resetForm(); setIsCreateDialogOpen(true); }}>
            <Plus className="w-4 h-4 mr-2" />
            Create Campaign
          </Button>
        </Card>
      ) : (
        <div className="grid gap-4">
          {campaigns.map((campaign) => (
            <Card key={campaign.id} data-testid={`card-sms-campaign-${campaign.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between gap-2">
                  <CardTitle className="text-base">{campaign.name}</CardTitle>
                  {getStatusBadge(campaign.status)}
                </div>
                <CardDescription className="flex items-center gap-4 mt-1">
                  <span className="flex items-center gap-1">
                    <Users className="w-3 h-3" />
                    {campaign.audienceType}
                  </span>
                  {campaign.status === "completed" && (
                    <>
                      <span className="flex items-center gap-1">
                        <CheckCircle className="w-3 h-3 text-green-500" />
                        {campaign.sentCount} sent
                      </span>
                      {campaign.failedCount > 0 && (
                        <span className="flex items-center gap-1">
                          <XCircle className="w-3 h-3 text-red-500" />
                          {campaign.failedCount} failed
                        </span>
                      )}
                    </>
                  )}
                  {campaign.scheduledFor && (
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatDate(campaign.scheduledFor)}
                    </span>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-2">
                <p className="text-sm text-muted-foreground line-clamp-2 mb-4 font-mono bg-muted p-2 rounded">
                  {campaign.messageTemplate}
                </p>
                <div className="flex gap-2 flex-wrap">
                  <Button variant="outline" size="sm" onClick={() => handlePreview(campaign)} data-testid={`button-preview-${campaign.id}`}>
                    <Eye className="w-3 h-3 mr-1" />
                    Preview
                  </Button>
                  
                  {campaign.status === "draft" && (
                    <>
                      <Button variant="outline" size="sm" onClick={() => openEditDialog(campaign)} data-testid={`button-edit-${campaign.id}`}>
                        <Pencil className="w-3 h-3 mr-1" />
                        Edit
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => queueCampaignMutation.mutate(campaign.id)} 
                        disabled={queueCampaignMutation.isPending} data-testid={`button-queue-${campaign.id}`}>
                        <Users className="w-3 h-3 mr-1" />
                        Queue Recipients
                      </Button>
                      <Button variant="destructive" size="sm" onClick={() => deleteCampaignMutation.mutate(campaign.id)}
                        disabled={deleteCampaignMutation.isPending} data-testid={`button-delete-${campaign.id}`}>
                        <Trash2 className="w-3 h-3 mr-1" />
                        Delete
                      </Button>
                    </>
                  )}
                  
                  {(campaign.status === "sending" || campaign.status === "scheduled") && (
                    <>
                      <Button size="sm" onClick={() => sendCampaignMutation.mutate(campaign.id)}
                        disabled={sendCampaignMutation.isPending} data-testid={`button-send-${campaign.id}`}>
                        <Send className="w-3 h-3 mr-1" />
                        Send Now
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => pauseCampaignMutation.mutate(campaign.id)}
                        disabled={pauseCampaignMutation.isPending} data-testid={`button-pause-${campaign.id}`}>
                        <Pause className="w-3 h-3 mr-1" />
                        Pause
                      </Button>
                    </>
                  )}
                  
                  {campaign.status === "paused" && (
                    <Button size="sm" onClick={() => resumeCampaignMutation.mutate(campaign.id)}
                      disabled={resumeCampaignMutation.isPending} data-testid={`button-resume-${campaign.id}`}>
                      <Play className="w-3 h-3 mr-1" />
                      Resume
                    </Button>
                  )}

                  {campaign.failedCount > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openRetryDialog(campaign)}
                      data-testid={`button-retry-failed-${campaign.id}`}
                    >
                      <RotateCcw className="w-3 h-3 mr-1" />
                      Retry failed ({campaign.failedCount})
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={isCreateDialogOpen || isEditDialogOpen} onOpenChange={(open) => {
        if (!open) {
          setIsCreateDialogOpen(false);
          setIsEditDialogOpen(false);
          setSelectedCampaign(null);
        }
      }}>
        <DialogContent className="w-full sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isEditDialogOpen ? "Edit SMS Campaign" : "Create SMS Campaign"}</DialogTitle>
            <DialogDescription>
              Configure your text message campaign settings and message content.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Campaign Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Summer Sale Announcement"
                data-testid="input-campaign-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="audienceType">Audience</Label>
              <Select value={formData.audienceType} onValueChange={(v) => setFormData({ ...formData, audienceType: v as any })}>
                <SelectTrigger data-testid="select-audience-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="mixed">All Contacts (Leads & Clients)</SelectItem>
                  <SelectItem value="lead">Leads Only</SelectItem>
                  <SelectItem value="client">Clients Only</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="regionId">Region Filter (optional)</Label>
                <Select value={formData.filters.regionId} onValueChange={(v) => setFormData({ ...formData, filters: { ...formData.filters, regionId: v === "all" ? "" : v } })}>
                  <SelectTrigger data-testid="select-region-filter">
                    <SelectValue placeholder="All Regions" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Regions</SelectItem>
                    {regions.map((r) => (
                      <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="clientType">Client Type Filter (optional)</Label>
                <Select value={formData.filters.clientType} onValueChange={(v) => setFormData({ ...formData, filters: { ...formData.filters, clientType: v === "all" ? "" : v } })}>
                  <SelectTrigger data-testid="select-client-type-filter">
                    <SelectValue placeholder="All Client Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Client Types</SelectItem>
                    {clientTypes.filter(ct => ct.isActive).map((ct) => (
                      <SelectItem key={ct.id} value={ct.name}>{ct.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="messageTemplate">Message</Label>
              <Textarea
                id="messageTemplate"
                value={formData.messageTemplate}
                onChange={(e) => setFormData({ ...formData, messageTemplate: e.target.value })}
                placeholder="Type your SMS message here. Use {{firstName}}, {{lastName}}, or {{name}} for personalization."
                rows={4}
                className="font-mono"
                data-testid="textarea-message-template"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Variables: {"{{firstName}}"}, {"{{lastName}}"}, {"{{name}}"}</span>
                <span className={characterCount > 160 ? "text-yellow-600" : ""}>
                  {characterCount} characters · {segmentCount} segment{segmentCount > 1 ? "s" : ""}
                </span>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Send Mode</Label>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.sendMode === "scheduled"}
                    onCheckedChange={(checked) => setFormData({ ...formData, sendMode: checked ? "scheduled" : "now" })}
                    data-testid="switch-schedule"
                  />
                  <span className="text-sm">Schedule for later</span>
                </div>
              </div>
              {formData.sendMode === "scheduled" && (
                <Input
                  type="datetime-local"
                  value={formData.scheduledFor}
                  onChange={(e) => setFormData({ ...formData, scheduledFor: e.target.value })}
                  className="mt-2 max-w-xs"
                  data-testid="input-scheduled-for"
                />
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setIsCreateDialogOpen(false);
              setIsEditDialogOpen(false);
              setSelectedCampaign(null);
            }}>
              Cancel
            </Button>
            <Button 
              onClick={isEditDialogOpen ? handleUpdate : handleCreate}
              disabled={!formData.name || !formData.messageTemplate || createCampaignMutation.isPending || updateCampaignMutation.isPending}
              data-testid="button-save-campaign"
            >
              {(createCampaignMutation.isPending || updateCampaignMutation.isPending) && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {isEditDialogOpen ? "Update Campaign" : "Create Campaign"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={isPreviewDialogOpen} onOpenChange={setIsPreviewDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Preview Recipients: {selectedCampaign?.name}</DialogTitle>
            <DialogDescription>
              Review who will receive this SMS campaign.
            </DialogDescription>
          </DialogHeader>

          {isPreviewLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : previewStats ? (
            <>
              <div className="grid grid-cols-4 gap-4 mb-4">
                <Card className="p-3 text-center">
                  <div className="text-2xl font-bold">{previewStats.total}</div>
                  <div className="text-xs text-muted-foreground">Total</div>
                </Card>
                <Card className="p-3 text-center bg-green-50 dark:bg-green-900/20">
                  <div className="text-2xl font-bold text-green-600">{previewStats.valid}</div>
                  <div className="text-xs text-muted-foreground">Will Receive</div>
                </Card>
                <Card className="p-3 text-center bg-orange-50 dark:bg-orange-900/20">
                  <div className="text-2xl font-bold text-orange-600">{previewStats.suppressed}</div>
                  <div className="text-xs text-muted-foreground">Suppressed</div>
                </Card>
                <Card className="p-3 text-center bg-red-50 dark:bg-red-900/20">
                  <div className="text-2xl font-bold text-red-600">{previewStats.invalid}</div>
                  <div className="text-xs text-muted-foreground">Invalid Phone</div>
                </Card>
              </div>

              <ScrollArea className="h-[300px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {previewRecipients.map((r, i) => (
                      <TableRow key={i}>
                        <TableCell>{r.displayName}</TableCell>
                        <TableCell className="font-mono text-sm">{r.phoneE164 || r.rawPhone || "—"}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{r.entityType}</Badge>
                        </TableCell>
                        <TableCell>
                          {r.isSuppressed ? (
                            <Badge className="bg-orange-100 text-orange-700">Suppressed</Badge>
                          ) : r.phoneE164 ? (
                            <Badge className="bg-green-100 text-green-700">Ready</Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-700">Invalid Phone</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </>
          ) : (
            <p className="text-muted-foreground text-center py-8">No data available</p>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPreviewDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Suppressions Dialog */}
      <Dialog open={isSuppressionsDialogOpen} onOpenChange={setIsSuppressionsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>SMS Suppression List</DialogTitle>
            <DialogDescription>
              Phone numbers that will not receive SMS campaigns. These are typically from opt-out replies (STOP).
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="h-[400px]">
            {suppressions.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No suppressed phone numbers</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Phone</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {suppressions.map((s) => (
                    <TableRow key={s.id}>
                      <TableCell className="font-mono">{s.phoneE164}</TableCell>
                      <TableCell>{s.reason}</TableCell>
                      <TableCell>{s.source}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{formatDate(s.createdAt)}</TableCell>
                      <TableCell>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => removeSuppression.mutate(s.phoneE164)}
                          disabled={removeSuppression.isPending}
                          data-testid={`button-unsuppress-${s.id}`}
                        >
                          Remove
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </ScrollArea>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsSuppressionsDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit & Retry Failed dialog. Lets the user fix the message template
          on a campaign that already shipped, and re-queue the recipients
          that the SMS scheduler marked failed. */}
      <Dialog open={retryDialogOpen} onOpenChange={(open) => {
        setRetryDialogOpen(open);
        if (!open) {
          setRetryCampaign(null);
          setRetryGuardTokens(null);
        }
      }}>
        <DialogContent className="w-full sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Edit message & retry failed</DialogTitle>
            <DialogDescription>
              {retryCampaign
                ? `Re-queue the ${retryCampaign.failedCount} failed recipient${
                    retryCampaign.failedCount === 1 ? "" : "s"
                  } on "${retryCampaign.name}". Edits to the template apply to the campaign and to the retry.`
                : ""}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="retry-template">Message template</Label>
              <Textarea
                id="retry-template"
                rows={6}
                value={retryTemplate}
                onChange={(e) => setRetryTemplate(e.target.value)}
                className="font-mono text-xs"
                data-testid="textarea-sms-retry-template"
              />
              <p className="text-[11px] text-muted-foreground">
                Available variables: {"{{firstName}}"}, {"{{lastName}}"}, {"{{name}}"}, {"{{phone}}"}.
              </p>
            </div>

            {retryGuardTokens && retryGuardTokens.length > 0 && (
              <div className="rounded border border-red-500/30 bg-red-500/5 p-2">
                <p className="text-xs text-red-600 dark:text-red-400" data-testid="text-sms-retry-guard-tokens">
                  Still leaking: {retryGuardTokens.join(", ")}
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRetryDialogOpen(false)}
              data-testid="button-cancel-sms-retry"
            >
              Cancel
            </Button>
            <Button
              disabled={!retryCampaign || retryFailedSmsMutation.isPending}
              onClick={() => {
                if (!retryCampaign) return;
                retryFailedSmsMutation.mutate({
                  id: retryCampaign.id,
                  messageTemplate: retryTemplate,
                });
              }}
              data-testid="button-confirm-sms-retry"
            >
              {retryFailedSmsMutation.isPending && (
                <Loader2 className="w-3 h-3 mr-2 animate-spin" />
              )}
              Save & retry failed
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
